import { Link } from 'react-router';
import { ArrowRight, Check, ChevronDown } from 'lucide-react';
import { Button } from '@dzseguidores/ui';
import { useProducts } from '@/hooks/use-catalog';
import { useSeo } from '@/hooks/use-seo';
import { ProductCard } from '@/components/cro/product-card';
import { TrustBar } from '@/components/cro/trust-bar';
import { ReviewsGrid } from '@/components/cro/social-proof';
import { NetworkIcon } from '@/components/network-icon';
import {
  HOME_FAQS,
  SERVICE_LANDINGS,
  breadcrumbJsonLd,
  faqJsonLd,
  itemListJsonLd,
  organizationJsonLd,
  type ServiceLandingKey,
} from '@/lib/seo';
import type { ProductPublic } from '@dzseguidores/types';

type Props = { landingKey: ServiceLandingKey };

export function ServiceLandingPage({ landingKey }: Props) {
  const landing = SERVICE_LANDINGS[landingKey];
  const { data, isLoading } = useProducts(landing.network);
  const products: ProductPublic[] = (data?.items ?? []).filter((p) =>
    landing.serviceType ? p.serviceTypeSlug === landing.serviceType : true,
  );

  const networkLabel = landing.network === 'instagram' ? 'Instagram' : 'TikTok';
  const serviceLabel =
    landing.serviceType === 'followers'
      ? 'seguidores'
      : landing.serviceType === 'likes'
        ? 'curtidas'
        : 'seguidores e curtidas';

  useSeo({
    title: landing.title,
    description: landing.description,
    path: landing.path,
    keywords: landing.keywords,
    jsonLd: [
      organizationJsonLd(),
      faqJsonLd([...HOME_FAQS]),
      breadcrumbJsonLd([
        { name: 'Início', path: '/' },
        { name: networkLabel, path: `/${landing.network}` },
        ...(landing.serviceType ? [{ name: serviceLabel, path: landing.path }] : []),
      ]),
      ...(products.length
        ? [
            itemListJsonLd(
              products.slice(0, 12).map((p, i) => ({
                name: p.name,
                slug: p.slug,
                position: i + 1,
              })),
              landing.h1,
            ),
          ]
        : []),
    ],
  });

  return (
    <div className="container-page py-10 md:py-14">
      <nav aria-label="Breadcrumb" className="mb-6 text-sm text-muted">
        <ol className="flex flex-wrap items-center gap-1.5">
          <li>
            <Link to="/" className="hover:text-fg">
              Início
            </Link>
          </li>
          <li aria-hidden>/</li>
          <li>
            <Link to={`/${landing.network}`} className="hover:text-fg">
              {networkLabel}
            </Link>
          </li>
          {landing.serviceType && (
            <>
              <li aria-hidden>/</li>
              <li className="text-fg">{serviceLabel}</li>
            </>
          )}
        </ol>
      </nav>

      <header className="max-w-3xl">
        <p className="mb-3 font-display text-sm font-semibold uppercase tracking-[0.2em] text-muted">
          DZ Seguidores · {networkLabel}
        </p>
        <h1 className="text-3xl font-semibold leading-tight sm:text-4xl lg:text-5xl">
          {landing.h1}
        </h1>
        <p className="mt-4 text-lg text-muted">{landing.intro}</p>
        <div className="mt-8 flex flex-wrap gap-3">
          <a href="#pacotes">
            <Button size="lg">
              Ver pacotes
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </a>
          <Link to={landing.network === 'instagram' ? '/tiktok' : '/instagram'}>
            <Button variant="secondary" size="lg">
              Ver {landing.network === 'instagram' ? 'TikTok' : 'Instagram'}
            </Button>
          </Link>
        </div>
      </header>

      <ul className="mt-10 grid gap-3 sm:grid-cols-2">
        {landing.bullets.map((b) => (
          <li key={b} className="flex items-start gap-2 text-sm text-muted">
            <Check className="mt-0.5 h-4 w-4 shrink-0 text-fg" />
            <span>{b}</span>
          </li>
        ))}
      </ul>

      {!landing.serviceType && (
        <section className="mt-14">
          <h2 className="text-xl font-semibold">Escolha: seguidores ou curtidas {networkLabel}</h2>
          <p className="mt-2 max-w-2xl text-muted">
            Páginas específicas para quem busca {serviceLabel} no {networkLabel}.
          </p>
          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            <Link
              to={`/${landing.network}/seguidores`}
              className="rounded-xl border border-border p-5 transition hover:border-fg/30"
            >
              <div className="mb-3 flex items-center gap-2">
                <NetworkIcon slug={landing.network} name={networkLabel} size={20} />
                <span className="font-semibold">Seguidores {networkLabel}</span>
              </div>
              <p className="text-sm text-muted">
                Compre seguidores {networkLabel} para crescer sua audiência com entrega monitorada.
              </p>
            </Link>
            <Link
              to={`/${landing.network}/curtidas`}
              className="rounded-xl border border-border p-5 transition hover:border-fg/30"
            >
              <div className="mb-3 flex items-center gap-2">
                <NetworkIcon slug={landing.network} name={networkLabel} size={20} />
                <span className="font-semibold">Curtidas {networkLabel}</span>
              </div>
              <p className="text-sm text-muted">
                Compre likes e curtidas {networkLabel} para impulsionar posts e vídeos.
              </p>
            </Link>
          </div>
        </section>
      )}

      <section id="pacotes" className="mt-14 scroll-mt-24">
        <h2 className="text-xl font-semibold">
          Pacotes de {serviceLabel} {networkLabel}
        </h2>
        <p className="mt-2 text-muted">
          Compare volumes e preços — pacotes recomendados aparecem em destaque.
        </p>

        {isLoading && <p className="mt-6 text-muted">Carregando pacotes…</p>}

        {!isLoading && products.length === 0 && (
          <p className="mt-6 rounded-xl border border-border p-4 text-sm text-muted">
            Em breve novos pacotes de {serviceLabel} {networkLabel}. Enquanto isso, confira o{' '}
            <Link to="/catalogo" className="underline hover:text-fg">
              catálogo completo
            </Link>
            .
          </p>
        )}

        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {products.map((product, i) => (
            <ProductCard key={product.id} product={product} peers={products} index={i} cta="buy" />
          ))}
        </div>
      </section>

      <section className="mt-16">
        <h2 className="text-xl font-semibold">Confiança na compra</h2>
        <TrustBar className="mt-6" />
      </section>

      <section className="mt-16">
        <h2 className="text-xl font-semibold">Depoimentos</h2>
        <div className="mt-6">
          <ReviewsGrid />
        </div>
      </section>

      <section className="mt-16">
        <h2 className="text-xl font-semibold">Perguntas frequentes</h2>
        <div className="mt-4 divide-y divide-border rounded-xl border border-border">
          {HOME_FAQS.map((f) => (
            <details key={f.q} className="group px-4 py-3">
              <summary className="flex cursor-pointer list-none items-center justify-between gap-3 font-medium">
                {f.q}
                <ChevronDown className="h-4 w-4 shrink-0 transition group-open:rotate-180" />
              </summary>
              <p className="mt-2 pb-2 text-sm text-muted">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      <div className="fixed inset-x-0 bottom-0 z-50 border-t border-border glass p-3 md:hidden">
        <a href="#pacotes" className="block">
          <Button className="w-full" size="lg">
            Ver pacotes {networkLabel}
          </Button>
        </a>
      </div>
      <div className="h-16 md:hidden" aria-hidden />
    </div>
  );
}
